import { axios } from '../request'
import { ARRAY_SHAPE, OBJECT_SHAPE, readEnvelope } from '../lib/apiEnvelope'

// #415 / #416 — a 2xx whose body is not the `{"data": …}` envelope
// (`API/response.go`) leaves `res.data.data` undefined, and committing that took
// the state out of the type it was born with: `membershipsPerYear` went from
// `[]` to `undefined`, and `AdminDashboard.vue`'s `v-if` then read `.length` off
// it and killed the render for the rest of the browsing session.
//
// It is not a hypothetical body. axios (^1.15.0) parses with
// `silentJSONParsing` on by default and `request.js` sets no `transitional`
// override, so a body that fails `JSON.parse` — empty, truncated, or the HTML a
// proxy serves — comes back through `.then()` as a plain **string** instead of
// rejecting. The success path is exactly where the malformed answer arrives.
//
// What the API actually returned is not recoverable from the two events: the
// one behind #415 has the SSO ticket endpoint answering 2xx with no ticket in
// it (`TICKET_EMPTY`), the one behind #416 has it not answering at all
// (`TICKET_UNREACHABLE`). The fix does not depend on knowing which — and the
// ticket path survived both because it guards its read (`ssoSession.js`), which
// is the whole difference.
//
// The legitimate failures do not explain the crash either: the empty-result 404
// (`API/checkEmpty.go`), 401, and network errors all reject, and the old
// `.catch(() => {})` swallowed them leaving the state untouched.
//
// So the invariant lives here: every consumer already assumes the four lists
// are arrays (`Overview.vue`'s `.forEach` and `v-for`s, `AdminDashboard`'s
// `.length`) and that the fifth is an object (`.year`, `.memberCount`,
// `.amount`), and the store is what must keep that true whatever the API
// answers.
//
// #419 — the shapes and the read moved to `lib/apiEnvelope` when `generateStore`
// needed the same guard for nineteen reads. Keeping a second copy here would
// re-open the trap this fix exists to close.

export default (_app) => {
  return {
    namespaced: true,
    state: {
      owedPerYear: [],
      nextYearQuotaExpectations: {},
      paidPerYear: [],
      canceledPerYear: [],
      membershipsPerYear: [],
    },
    mutations: {
      setOwedPerYear(state, owedPerYear) {
        state.owedPerYear = owedPerYear
      },
      setNextYearQuotaExpectations(state, owedPerYear) {
        state.nextYearQuotaExpectations = owedPerYear
      },
      setPaidPerYear(state, paidPerYear) {
        state.paidPerYear = paidPerYear
      },
      setCanceledPerYear(state, canceledPerYear) {
        state.canceledPerYear = canceledPerYear
      },
      setMembershipsPerYear(state, membershipsPerYear) {
        state.membershipsPerYear = membershipsPerYear
      },
    },
    actions: {
      getOwedPerYear(context) {
        return fetchReport(context, {
          url: '/reports/owedPerYear',
          mutation: 'setOwedPerYear',
          shape: ARRAY_SHAPE,
        })
      },
      getNextYearQuotaExpectations(context) {
        return fetchReport(context, {
          url: '/reports/nextYearQuotaExpectations',
          mutation: 'setNextYearQuotaExpectations',
          shape: OBJECT_SHAPE,
        })
      },
      getPaidPerYear(context) {
        return fetchReport(context, {
          url: '/reports/paymentsPerYear',
          mutation: 'setPaidPerYear',
          shape: ARRAY_SHAPE,
        })
      },
      getCanceledPerYear(context) {
        return fetchReport(context, {
          url: '/reports/cancelationsPerYear',
          mutation: 'setCanceledPerYear',
          shape: ARRAY_SHAPE,
        })
      },
      getMembershipsPerYear(context) {
        return fetchReport(context, {
          url: '/reports/membershipsPerYear',
          mutation: 'setMembershipsPerYear',
          shape: ARRAY_SHAPE,
        })
      },
    },
  }
}

// One shape for all five, so the trap above can only exist in one place.
//
// The `.catch(() => {})` this replaces was mute (#415), which is why a broken
// API surfaced as a render TypeError and nothing else. Both branches now say
// what happened, and both settle: never resolving is what pinned
// `AdminDashboard`'s `showMembershipsGraph` to false for the rest of the
// session, because the `.then()` that sets it true could not run.
function fetchReport(context, { url, mutation, shape }) {
  return new Promise((resolve) => {
    axios
      .get(url)
      .then((res) => {
        // The case that took the dashboard down, and the one that used to leave
        // no trace at all: the request succeeded, so there was no error to
        // swallow — the payload simply was not there. `readEnvelope` says so and
        // hands back the empty value of the shape, which is committed either
        // way so the state cannot leave its type.
        const { data } = readEnvelope(res, shape, `reports: ${url}`)
        context.commit(mutation, data)
        resolve()
      })
      .catch((err) => {
        // A 404 is how these endpoints report "no rows" (API.CheckEmpty), not a
        // fault: the state already holds the empty value, and logging it would
        // shout on every organisation that has no data yet.
        if (err?.response?.status !== 404) {
          console.error(`reports: ${url} failed`, err)
        }
        // Deliberately without writing the empty value: a transient failure on
        // a re-fetch must not wipe a report that was already loaded.
        resolve()
      })
  })
}
